<script setup lang="ts">
import dayjs from 'dayjs';

const props = withDefaults(
  defineProps<{
    label?: string;
    format?: string;
    disabled?: boolean;
    rules?: any;
    placeholder?: string;
    maxDate?: Date;
    minDate?: Date;
    errorMessages?: any;
    hideDetails?: boolean | 'auto';
    appendIcon?: string;
    closeOnSelect?: boolean;
    allowTyping?: boolean;
    allowSelectingDates?: boolean;
  }>(),
  {
    label: 'Date',
    format: 'DD/MM/YYYY',
    disabled: false,
    rules: [],
    errorMessages: '',
    placeholder: 'DD/MM/YYYY',
    minDate: undefined,
    maxDate: undefined,
    hideDetails: 'auto',
    appendIcon: 'mdi-calendar',
    closeOnSelect: true,
    allowTyping: false,
    allowSelectingDates: true,
  }
);

const menu = ref(false);
const isTyping = ref(false);
const typedValue = ref('');


const date = defineModel<string>({
  default: '',
});

const dateFormatted = computed(() => {
  if (isTyping.value) {
    return typedValue.value;
  }
  return date.value ? dayjs(date.value).format(props.format) : '';
});

// Format a raw input into DD/MM/YYYY mask using only digits
const applyDateMask = (raw: string): string => {
  const digitsOnly = raw.replace(/\D/g, '').slice(0, 8);
  const day = digitsOnly.slice(0, 2);
  const month = digitsOnly.slice(2, 4);
  const year = digitsOnly.slice(4, 8);

  let masked = day;
  if (month) masked += `/${month}`;
  if (year) masked += `/${year}`;
  return masked;
};

const onUpdate = (): void => {
  if (props.closeOnSelect) {
    menu.value = false;
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (!props.allowTyping || props.allowSelectingDates) return;

  const target = e.target as HTMLInputElement | null;
  const allowedControlKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Home',
    'End',
    'Enter',
  ];

  // Allow shortcuts like Ctrl/Cmd + A/C/V/X
  const isShortcut = (e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());
  if (allowedControlKeys.includes(e.key) || isShortcut) return;

  const isDigit = e.key >= '0' && e.key <= '9';
  if (!isDigit) {
    e.preventDefault();
    return;
  }

  // Enforce a maximum of 8 digits (DDMMYYYY)
  const currentDigits = (target?.value ?? '').replace(/\D/g, '');
  if (currentDigits.length >= 8) {
    e.preventDefault();
  }
};

const onPaste = (e: ClipboardEvent) => {
  if (!props.allowTyping || props.allowSelectingDates) return;
  const pasted = e.clipboardData?.getData('text') ?? '';
  const digits = pasted.replace(/\D/g, '').slice(0, 8);
  const masked = applyDateMask(digits);
  const target = e.target as HTMLInputElement | null;
  if (target) target.value = masked;
  // Reuse onInput flow to parse/commit when complete
  onInput(masked);
};

const onInput = (value: string | InputEvent) => {
  // Only allow typing if allowTyping is true and allowSelectingDates is false
  if (!props.allowTyping || props.allowSelectingDates) return;
  
  // Support both direct string values and InputEvent payloads
  const nextValue =
    typeof value === 'string'
      ? value
      : (value?.target as HTMLInputElement | null)?.value ?? '';

  isTyping.value = true;
  // Enforce DD/MM/YYYY mask while typing
  const maskedValue = applyDateMask(nextValue);
  typedValue.value = maskedValue;
  
  // Try to parse the typed date
  if (maskedValue.length === 10) {
    const parsedDate = dayjs(maskedValue, props.format, true);
    if (!parsedDate.isValid()) return;
    date.value = parsedDate.format('YYYY-MM-DD');
    isTyping.value = false;
    typedValue.value = '';
  }
};

const onBlur = () => {
  // Only handle blur for typing mode
  if (!props.allowTyping || props.allowSelectingDates) return;
  
  if (isTyping.value && typedValue.value) {
    const parsedDate = dayjs(typedValue.value, props.format, true);
    if (parsedDate.isValid()) {
      date.value = parsedDate.format('YYYY-MM-DD');
    } else {
      // Reset to original value if invalid
      typedValue.value = date.value ? dayjs(date.value).format(props.format) : '';
    }
    isTyping.value = false;
  }
};

const onFocus = () => {
  // Only handle focus for typing mode
  if (!props.allowTyping || props.allowSelectingDates) return;
  
  if (date.value) {
    typedValue.value = dayjs(date.value).format(props.format);
    isTyping.value = true;
  }
};
</script>
<template>
  <div>
    <!-- Date Picker Mode (allowSelectingDates = true) -->
    <v-menu 
      v-if="allowSelectingDates && !allowTyping"
      v-model="menu" 
      :close-on-content-click="false" 
      :nudge-right="40" 
      transition="scale-transition" 
      offset-y
    >
      <template #activator="{ props: menuProps }">
        <v-text-field
          :label="label"
          :placeholder="placeholder"
          :input-placeholder="placeholder"
          :disabled="disabled"
          :hide-actions="true"
          :rules="rules"
          :hide-details="hideDetails"
          variant="solo-filled"
          :readonly="true"
          :model-value="dateFormatted"
          :append-inner-icon="appendIcon"
          v-bind="menuProps"
          :error-messages="errorMessages"
          flat
        />
      </template>
      <v-date-picker
        v-model="date"
        :input-placeholder="placeholder"
        input-mode="calendar"
        :min="minDate"
        :max="maxDate"
        :format="format"
        @update:modelValue="onUpdate"
        @click:cancel="menu = false"
        @click:save="menu = false"
      />
    </v-menu>

    <!-- Typing Mode (allowTyping = true) -->
    <v-text-field
      v-else-if="allowTyping && !allowSelectingDates"
      :label="label"
      :placeholder="placeholder"
      :input-placeholder="placeholder"
      :disabled="disabled"
      :hide-actions="true"
      :rules="rules"
      :hide-details="hideDetails"
      variant="solo-filled"
      :readonly="false"
      :model-value="dateFormatted"
      :append-inner-icon="appendIcon"
      :error-messages="errorMessages"
      type="tel"
      inputmode="numeric"
      :maxlength="10"
      flat
      @update:modelValue="onInput"
      @keydown="onKeydown"
      @paste.prevent="onPaste"
      @blur="onBlur"
      @focus="onFocus"
    />

    <!-- Fallback: Default to date picker if both are false or both are true -->
    <v-menu 
      v-else
      v-model="menu" 
      :close-on-content-click="false" 
      :nudge-right="40" 
      transition="scale-transition" 
      offset-y
    >
      <template #activator="{ props: menuProps }">
        <v-text-field
          :label="label"
          :placeholder="placeholder"
          :input-placeholder="placeholder"
          :disabled="disabled"
          :hide-actions="true"
          :rules="rules"
          :hide-details="hideDetails"
          variant="solo-filled"
          :readonly="true"
          :model-value="dateFormatted"
          :append-inner-icon="appendIcon"
          v-bind="menuProps"
          :error-messages="errorMessages"
          flat
        />
      </template>
      <v-date-picker
        v-model="date"
        :input-placeholder="placeholder"
        input-mode="calendar"
        :min="minDate"
        :max="maxDate"
        :format="format"
        @update:modelValue="onUpdate"
        @click:cancel="menu = false"
        @click:save="menu = false"
      />
    </v-menu>
  </div>
</template>
